import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2400);
    } catch (e2) {
      toast.error(
        e2?.response?.data?.detail ||
          (!e2?.response
            ? "Couldn't reach the server. Please try again."
            : "Could not reset password. Please request a new link.")
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="bg-brand-cream min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full bg-white rounded-3xl border border-brand-border-warm p-8 shadow-warm">
          <h1 className="font-heading text-2xl font-black text-brand-ink">Invalid reset link</h1>
          <p className="text-sm text-brand-ink/70 mt-2">
            This link is missing a reset token. Request a new one to continue.
          </p>
          <Link
            to="/forgot-password"
            data-testid="reset-request-new"
            className="mt-6 inline-flex bg-brand-forest text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-forest-dark"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-3xl border border-brand-border-warm p-8 shadow-warm animate-fade-in-up">
        <Link
          to="/login"
          data-testid="reset-back-to-login"
          rel="nofollow"
          className="inline-flex items-center gap-1.5 text-sm text-brand-ink/60 hover:text-brand-forest mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        {done ? (
          <div data-testid="reset-success" className="text-center">
            <span className="inline-flex w-14 h-14 rounded-full bg-brand-light-green text-brand-forest items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </span>
            <h1 className="font-heading text-2xl font-black text-brand-ink">Password updated!</h1>
            <p className="text-sm text-brand-ink/70 mt-3">Redirecting you to login…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-black text-brand-ink">Set a new password</h1>
            <p className="text-sm text-brand-ink/70 mt-2 leading-relaxed">
              Choose a strong password you haven't used before.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/40" />
                <input
                  data-testid="reset-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 8 chars)"
                  className="w-full pl-11 pr-12 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
                <button
                  type="button"
                  data-testid="reset-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-brand-ink/50 hover:text-brand-ink hover:bg-white/60 flex items-center justify-center transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/40" />
                <input
                  data-testid="reset-password-confirm"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full pl-11 pr-4 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
              </div>
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading}
                className="w-full bg-brand-forest text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Update password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
